// eatRecipeModal.js
import React, { useState, useEffect } from 'react';
import { consumeApi } from '../../../api/consumeApi.js';
import { catalogApi } from '../../../api/catalogApi.js';
import { Modal } from '../../shared';

const EatRecipeModal = ({ initialRecipeName, onClose, onSubmit, useInternalModal = true }) => {
  const [recipes, setRecipes] = useState([]); // [{ name }]
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [recipeName, setRecipeName] = useState(initialRecipeName ?? '');
  const [dateTime, setDateTime] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  });
  const [servings, setServings] = useState('1');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await catalogApi.getRecipes();

        // Accept multiple shapes, including array of strings:
        // { data: { rows: { recipes: [] } } }
        // { data: { recipes: [] } }
        // { recipes: [] }
        // Or final array directly
        const root =
          resp?.data?.rows ??
          resp?.data ??
          resp ??
          [];

        const list = Array.isArray(root)
          ? root
          : Array.isArray(root.recipes)
          ? root.recipes
          : Array.isArray(root.rows)
          ? root.rows
          : Array.isArray(root?.rows?.recipes)
          ? root.rows.recipes
          : [];

        const mapped = list
          .map(r => {
            if (typeof r === 'string') {
              return { name: r };
            }
            return {
              name: r?.recipeName ?? r?.name, // prefer explicit recipeName
            };
          })
          .filter(m => !!m.name);

        if (!cancelled) {
          setRecipes(mapped);

          // Ensure a selection
          if (initialRecipeName && mapped.some(m => String(m.name) === String(initialRecipeName))) {
            setRecipeName(String(initialRecipeName));
          } else if (!initialRecipeName && mapped.length) {
            setRecipeName(String(mapped[0].name));
          } else if (!mapped.length) {
            setRecipeName('');
          }
        }
      } catch (e) {
        console.error('catalogApi.getRecipes error', e);
        if (!cancelled) setError('Failed to load recipes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialRecipeName]);

  const servingsNum = Number(servings);
  const isValid = !!recipeName && !!dateTime && !Number.isNaN(servingsNum) && servingsNum > 0;

  const handleSubmit = async () => {
    if (!isValid) return;

    const eatenOn = dateTime.replace('T', ' ');
    const payload = {
      recipeName: String(recipeName),
      eatenOn: eatenOn, // local datetime string from input
      servingsEaten: servingsNum,
    };

    // Mirror EatItem flow with a recipe endpoint
    const response = await consumeApi.eatRecipe(payload);
    if (response?.status === true) {
      console.log('Success');
    } else {
      console.error('Error creating the recipe consumption:', response?.msg);
    }

    onSubmit?.(payload);
    onClose?.();
  };

  const content = (
    <>
      {loading && <div style={{ opacity: 0.7, fontStyle: 'italic' }}>Loading…</div>}
      {error && <div style={{ color: '#b00020', marginBottom: 8 }}>{error}</div>}

      {!loading && !error && (
        <div style={{ display: 'grid', gap: 12, maxWidth: 520 }}>
          {/* Recipe selector */}
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>Recipe</span>
            <select
              value={recipeName}
              onChange={(e) => setRecipeName(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #ddd',
              }}
            >
              {recipes.map(r => (
                <option key={r.name} value={r.name}>{r.name}</option>
              ))}
            </select>
            {recipes.length === 0 && (
              <small style={{ opacity: 0.7 }}>No recipes available.</small>
            )}
          </label>

          {/* Date/Time */}
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>Date &amp; Time</span>
            <input
              type="datetime-local"
              value={dateTime}
              onChange={(e) => setDateTime(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #ddd',
              }}
            />
          </label>

          {/* Servings */}
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>Servings</span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              min="0.0000001"
              value={servings}
              onChange={(e) => setServings(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #ddd',
              }}
            />
            <small style={{ opacity: 0.7 }}>
              Must be a number greater than 0. Decimals allowed.
            </small>
          </label>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid #ccc',
                padding: '8px 12px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              disabled={!isValid}
              onClick={handleSubmit}
              style={{
                background: isValid ? '#1E88E5' : '#90CAF9',
                color: '#fff',
                border: 'none',
                padding: '8px 14px',
                borderRadius: 8,
                cursor: isValid ? 'pointer' : 'not-allowed',
                fontWeight: 600,
              }}
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (useInternalModal) {
    return (
      <Modal title="Eat Recipe" onClose={onClose}>
        {content}
      </Modal>
    );
  }

  // Embedded mode: return just the content (no wrapper Modal)
  return content;
};

export { EatRecipeModal };
